import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const esc = (s) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const html = (name, msg) => `<div style="font-family:Arial,sans-serif;font-size:14px;color:#1a1a1a;margin:16px">
<p>Dear ${esc(name)},</p>${msg.split(/\n\n/).filter((p) => p.trim()).map((p) => `<p>${esc(p.trim()).replace(/\n/g, "<br>")}</p>`).join("")}
<p><span style="display:inline-block;background:#0E1A3A;color:#fff;padding:10px 18px;border-radius:3px;">Set up Direct Debit</span></p>
<p style="font-size:12px;color:#555;">This is a secure GoCardless page. Your payments are protected by the Direct Debit Guarantee.</p>
<p>Kind regards,</p><p style="font-size:11px;color:#999;">[Your branded FBS signature and footer are added here]</p></div>`;

// Preview + edit the Direct Debit invite email before sending.
export function InviteDialog({ customer, open, onOpenChange, onSent }) {
  const [p, setP] = useState(null);
  const [msg, setMsg] = useState("");
  const [reminders, setReminders] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setP(null);
    api.get(`/gocardless/customers/${customer.customer_id}/invite-preview`)
      .then((r) => { setP(r.data); setMsg(r.data.message); setReminders(r.data.reminders_enabled !== false); })
      .catch((e) => { toast.error(formatErr(e.response?.data?.detail)); onOpenChange(false); });
  }, [open, customer.customer_id, onOpenChange]);

  async function send() {
    setBusy(true);
    try {
      const r = await api.post(`/gocardless/customers/${customer.customer_id}/invite`, { send_email: true, message: msg, reminders });
      r.data.emailed ? toast.success(`Invite emailed to ${p.to}${reminders ? " · daily reminders on" : ""}`) : toast.error("Email could not be sent — try again or use Copy invite link");
      if (r.data.emailed) { onOpenChange(false); onSent?.({ reminders_enabled: reminders, invited_at: new Date().toISOString(), reminder_count: 0 }); }
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setBusy(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="dd-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-strong">Direct Debit invite · {customer.name}</DialogTitle>
          <DialogDescription>Check and edit the wording. Changes apply to this email only.</DialogDescription>
        </DialogHeader>
        {!p ? <div className="h-64 flex items-center justify-center"><Loader2 className="animate-spin text-[var(--navy)]" /></div> : (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2 text-xs">
              <div><span className="text-[var(--ink-2)]">To:</span> <b data-testid="dd-invite-to">{p.to}</b></div>
              <div><span className="text-[var(--ink-2)]">Subject:</span> <span data-testid="dd-invite-subject">{p.subject}</span></div>
              <label className="block text-[var(--ink-2)] pt-1">Message</label>
              <textarea data-testid="dd-invite-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={10} maxLength={3000} className="input w-full text-sm" />
              <button type="button" data-testid="dd-invite-reset" onClick={() => setMsg(p.message)} className="text-[11px] underline text-[var(--ink-2)]">Reset to standard wording</button>
              <label className="flex items-center gap-2 pt-2 cursor-pointer">
                <input type="checkbox" data-testid="dd-invite-reminders" checked={reminders} onChange={(e) => setReminders(e.target.checked)} className="accent-[var(--navy)]" />
                <span>Send a reminder every day at 09:00 until the mandate is set up</span>
              </label>
            </div>
            <iframe title="Invite preview" srcDoc={html(p.name, msg)} className="w-full h-[380px] border border-[var(--line)] rounded-sm bg-white" data-testid="dd-invite-preview" />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} className="btn-ghost text-sm !py-2 !px-4" data-testid="dd-invite-cancel">Cancel</button>
          <button onClick={send} disabled={!p || busy || !msg.trim()} className="btn-primary text-sm !py-2 !px-4 flex items-center gap-1.5" data-testid="dd-invite-send">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send invite
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
